"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { AstroMascot } from "./AstroMascot";
import { 
  ArrowLeft, 
  ArrowRight,
  Check,
  Eye,
  Rocket,
  Sparkles,
  Clock,
  CheckCircle,
  FileText,
  GraduationCap,
  Shield,
  User,
  Briefcase,
  Send,
  Star,
  Phone,
  Mail,
  MapPin,
  Calendar,
  CreditCard,
  Building,
  Trophy,
  AlertCircle,
  Zap,
  Database
} from "lucide-react";
const openTurfLogo = '/openturf-logo.svg';

interface CompletedSection {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  completedAt: string;
  itemsCompleted: number;
}

interface ExtractedField {
  label: string;
  value: string;
  source: string;
  isRequired: boolean;
  isMissing?: boolean;
}

interface AstroFinalSubmissionProps {
  onSubmit: () => void;
  onShowTerms?: () => void;
  onBack: () => void;
  completedSections?: CompletedSection[];
  joinerType: 'intern' | 'consultant' | 'employee';
  experienceLevel: 'fresher' | 'experienced';
}

export function AstroFinalSubmission({ 
  onSubmit, 
  onShowTerms, 
  onBack,
  completedSections,
  joinerType,
  experienceLevel
}: AstroFinalSubmissionProps) {
  const [showPreview, setShowPreview] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [launchAnimation, setLaunchAnimation] = React.useState(false);

  // Default completed sections based on joiner type
  const getDefaultSections = (): CompletedSection[] => {
    const baseSections = [
      {
        id: 'identity',
        title: 'Identity Verification',
        description: 'Profile photo and ID documents verified',
        icon: User,
        completedAt: '2 minutes ago',
        itemsCompleted: 2
      }
    ];

    if (joinerType === 'intern') {
      return [
        ...baseSections,
        {
          id: 'nda',
          title: 'NDA Agreement',
          description: 'Non-disclosure agreement signed',
          icon: Shield,
          completedAt: '1 minute ago',
          itemsCompleted: 1
        }
      ];
    } else if (joinerType === 'consultant') {
      return [
        ...baseSections,
        {
          id: 'contract',
          title: 'Contract Agreement',
          description: 'Consulting contract signed',
          icon: FileText,
          completedAt: '1 minute ago',
          itemsCompleted: 1
        }
      ];
    } else {
      // Employee
      const sections = [
        ...baseSections,
        {
          id: 'education',
          title: 'Educational Background',
          description: 'Academic qualifications verified',
          icon: GraduationCap,
          completedAt: '3 minutes ago',
          itemsCompleted: 1
        }
      ];

      if (experienceLevel === 'experienced') {
        sections.push({
          id: 'professional',
          title: 'Professional Experience',
          description: 'Work history and salary details submitted',
          icon: Briefcase,
          completedAt: '1 minute ago',
          itemsCompleted: 3
        });
      }

      return sections;
    }
  };

  const sections = completedSections || getDefaultSections();
  const totalItems = sections.reduce((acc, section) => acc + section.itemsCompleted, 0);

  // Get mandatory fields based on joiner type and experience level
  const getMandatoryFields = (): ExtractedField[] => {
    const baseFields = [
      { label: 'Full Name', value: 'Aditya Rajesh Kumar', source: 'Aadhaar Card', isRequired: true },
      { label: 'Date of Birth', value: '15th March 1998', source: 'Aadhaar Card', isRequired: true },
      { label: 'Personal Email', value: 'aditya.kumar@gmail.com', source: 'Resume', isRequired: true },
      { label: 'Mobile Number', value: '+91 9876543210', source: 'Resume', isRequired: true },
      { label: 'PAN Number', value: 'ABCDE1234F', source: 'PAN Card', isRequired: true },
      { label: 'Passport Number', value: 'K1234567', source: 'Passport', isRequired: true }
    ];

    if (joinerType === 'intern') {
      return [
        ...baseFields,
        { label: 'Highest Qualification', value: 'B.Tech Computer Science', source: 'Degree Certificate', isRequired: true },
        { label: 'Institution', value: 'Indian Institute of Technology, Delhi', source: 'Degree Certificate', isRequired: true },
        { label: 'Graduation Year', value: '2020', source: 'Degree Certificate', isRequired: true },
        { label: 'Emergency Contact Name', value: 'Rajesh Kumar (Father)', source: 'Emergency Contact Form', isRequired: true },
        { label: 'Emergency Contact Phone', value: '+91 9876543211', source: 'Emergency Contact Form', isRequired: true }
      ];
    } else if (joinerType === 'consultant') {
      return [
        ...baseFields,
        { label: 'Current Residential Address', value: '123, Green Valley, Sector 15, Noida, UP - 201301', source: 'Address Proof', isRequired: true },
        { label: 'Total Professional Experience', value: '8 Years', source: 'Resume', isRequired: true },
        { label: 'Billing Rate/Consulting Fee', value: '', source: 'Rate Card', isRequired: true, isMissing: true },
        { label: 'Bank Account Number', value: '1234567890123456', source: 'Bank Statement', isRequired: true },
        { label: 'Bank IFSC Code', value: 'HDFC0001234', source: 'Bank Statement', isRequired: true }
      ];
    } else if (joinerType === 'employee') {
      const employeeBaseFields = [
        ...baseFields,
        { label: 'Current Residential Address', value: '456, Tech Park, HSR Layout, Bangalore, KA - 560102', source: 'Address Proof', isRequired: true },
        { label: 'Highest Qualification', value: 'M.Tech Software Engineering', source: 'Degree Certificate', isRequired: true },
        { label: 'Institution', value: 'Indian Institute of Science, Bangalore', source: 'Degree Certificate', isRequired: true },
        { label: 'Graduation Year', value: '2022', source: 'Degree Certificate', isRequired: true },
        { label: 'Emergency Contact Name', value: 'Priya Kumar (Spouse)', source: 'Emergency Contact Form', isRequired: true },
        { label: 'Emergency Contact Phone', value: '+91 9876543212', source: 'Emergency Contact Form', isRequired: true },
        { label: 'Bank Account Number', value: '9876543210987654', source: 'Bank Statement', isRequired: true },
        { label: 'Bank IFSC Code', value: 'ICICI0001234', source: 'Bank Statement', isRequired: true }
      ];

      if (experienceLevel === 'fresher') {
        return [
          ...employeeBaseFields,
          { label: 'CGPA/Percentage', value: '8.7 CGPA', source: 'Academic Transcript', isRequired: true }
        ];
      } else {
        return [
          ...employeeBaseFields,
          { label: 'Total Work Experience', value: '5 Years 3 Months', source: 'Resume', isRequired: true },
          { label: 'Last Company Name', value: 'Tech Innovations Pvt Ltd', source: 'Experience Letter', isRequired: true },
          { label: 'Last Designation', value: 'Senior Software Developer', source: 'Experience Letter', isRequired: true },
          { label: 'Employment Tenure', value: 'June 2019 - August 2024', source: 'Experience Letter', isRequired: true }
        ];
      }
    }

    return baseFields;
  };

  const extractedFields = getMandatoryFields();
  const missingFields = extractedFields.filter(field => field.isMissing || !field.value);
  const completedFields = extractedFields.filter(field => !field.isMissing && field.value);

  const handleSubmit = () => {
    setIsSubmitting(true);
    setLaunchAnimation(true);
    
    // Simulate submission delay
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setLaunchAnimation(false);
      
      // Auto-proceed after celebration
      setTimeout(() => {
        onSubmit();
      }, 3000);
    }, 2500);
  };

  const getJoinerInfo = () => {
    const typeMap = {
      intern: { title: 'Intern', color: 'bg-green-100 text-green-700' },
      consultant: { title: 'Consultant', color: 'bg-blue-100 text-blue-700' },
      employee: { title: 'Employee', color: 'bg-purple-100 text-purple-700' }
    };
    
    const expMap = {
      fresher: { title: 'Fresher', color: 'bg-yellow-100 text-yellow-700' },
      experienced: { title: 'Experienced', color: 'bg-indigo-100 text-indigo-700' }
    };

    return {
      type: typeMap[joinerType],
      experience: expMap[experienceLevel]
    };
  };

  const joinerInfo = getJoinerInfo();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 flex flex-col relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-20 left-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl"
          animate={{ 
            y: [0, -30, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-48 h-48 bg-blue-200/20 rounded-full blur-3xl"
          animate={{ 
            y: [0, 25, 0],
            scale: [1.1, 1, 1.1]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        
        {/* Launch animation background */}
        <AnimatePresence>
          {launchAnimation && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-gradient-to-t from-indigo-900/20 via-purple-800/10 to-transparent"
            >
              {/* Shooting stars */}
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-white rounded-full"
                  initial={{ 
                    x: Math.random() * window.innerWidth,
                    y: window.innerHeight + 50,
                    opacity: 0
                  }}
                  animate={{ 
                    y: -50,
                    opacity: [0, 1, 0]
                  }}
                  transition={{ 
                    duration: 1.5,
                    delay: i * 0.2,
                    ease: "easeOut"
                  }}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/70 backdrop-blur-sm border-b border-gray-200/50"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-gray-700 hover:text-gray-900 hover:bg-gray-100"
              disabled={isSubmitting}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-gray-300" />
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-gray-700">
              <Rocket className="w-5 h-5 text-purple-600" />
              <span>New Workflow - Step 5</span>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 p-6">
        <div className="max-w-4xl mx-auto relative z-10">
          {!isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
            >
              {/* Header */}
              <div className="text-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6"
                >
                  <div className="flex items-center justify-center space-x-4 mb-6">
                    <Badge className={joinerInfo.type.color}>
                      {joinerInfo.type.title}
                    </Badge>
                    <Badge className={joinerInfo.experience.color}>
                      {joinerInfo.experience.title}
                    </Badge>
                  </div>

                  <h1 className="text-gray-900 mb-4">
                    Final Review & Submission 🎯
                  </h1>
                  <p className="text-gray-600 max-w-2xl mx-auto">
                    You're almost there! Review your completed sections and submit for HR approval.
                  </p>
                </motion.div>

                {/* Progress */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mb-8"
                >
                  <div className="flex items-center justify-center space-x-4 mb-4">
                    <Badge variant="outline">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                      All Steps Complete
                    </Badge>
                    <Badge className="bg-green-100 text-green-700">
                      {totalItems} Documents Verified
                    </Badge>
                  </div>
                  <Progress value={100} className="h-2 max-w-md mx-auto" />
                </motion.div>
              </div>

              {/* Astro Celebration */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="max-w-3xl mx-auto"
              >
                <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 p-6">
                  <div className="flex items-start space-x-6">
                    <motion.div
                      animate={{ 
                        y: [0, -5, 0],
                        rotate: [0, 2, -2, 0]
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <AstroMascot expression="excited" size="lg" />
                    </motion.div>
                    
                    <div className="flex-1">
                      <div className="bg-white rounded-2xl p-6 border border-green-200 relative">
                        {/* Speech bubble pointer */}
                        <div className="absolute left-0 top-6 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-r-[12px] border-r-white -translate-x-3" />
                        
                        <p className="text-gray-800 leading-relaxed">
                          🎉 You've nailed it, star recruit! All systems are green and ready for launch. 
                          Let's send this off to Mission Control for the final approval! 
                          <span className="inline-flex items-center ml-2">
                            <Rocket className="w-4 h-4 text-purple-600" />
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>

              {/* Completed Sections Checklist */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="space-y-6"
              >
                <h3 className="text-gray-900 text-center">
                  Completed Sections ✨
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {sections.map((section, index) => (
                    <motion.div
                      key={section.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 + index * 0.1 }}
                    >
                      <Card className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 ring-2 ring-green-100">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-green-100 text-green-700">
                              <Check className="w-6 h-6" />
                            </div>
                            
                            <div className="flex-1">
                              <h4 className="text-gray-900 mb-1">{section.title}</h4>
                              <p className="text-gray-600 text-sm">{section.description}</p>
                            </div>
                          </div>

                          <motion.div
                            animate={{ rotate: [0, 360] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                          >
                            <Star className="w-5 h-5 text-yellow-500" />
                          </motion.div>
                        </div>

                        <div className="flex items-center justify-between text-sm text-gray-600 bg-white rounded-lg p-3">
                          <span>{section.itemsCompleted} items completed</span>
                          <span>{section.completedAt}</span>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Preview and Submit Actions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="space-y-6 text-center pt-6"
              >
                {/* Preview Button */}
                <Button
                  variant="outline"
                  onClick={() => setShowPreview(!showPreview)}
                  className="mx-auto text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showPreview ? 'Hide' : 'Preview'} Submitted Information
                </Button>

                {/* Enhanced Preview Panel */}
                <AnimatePresence>
                  {showPreview && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-6"
                    >
                      {/* AI Extraction Summary */}
                      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                              <Zap className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h4 className="text-gray-900">AI Data Extraction Summary</h4>
                              <p className="text-gray-600 text-sm">TurfAI has processed your documents and extracted information</p>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="bg-white rounded-lg p-3 text-center">
                            <div className="text-lg text-blue-600 mb-1">{extractedFields.length}</div>
                            <div className="text-xs text-gray-600">Total Fields</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 text-center">
                            <div className="text-lg text-green-600 mb-1">{completedFields.length}</div>
                            <div className="text-xs text-gray-600">Completed Fields</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 text-center">
                            <div className={`text-lg mb-1 ${missingFields.length === 0 ? 'text-green-600' : 'text-orange-600'}`}>
                              {missingFields.length}
                            </div>
                            <div className="text-xs text-gray-600">Missing Fields</div>
                          </div>
                        </div>

                        {missingFields.length === 0 ? (
                          <div className="flex items-center space-x-2 text-sm text-green-700 bg-green-100 rounded-lg p-3">
                            <CheckCircle className="w-4 h-4" />
                            <span>All mandatory fields completed - Ready for HR verification</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2 text-sm text-orange-700 bg-orange-100 rounded-lg p-3">
                            <AlertCircle className="w-4 h-4" />
                            <span>{missingFields.length} mandatory field(s) missing - Please review below</span>
                          </div>
                        )}
                      </Card>

                      {/* Missing Fields Warning */}
                      {missingFields.length > 0 && (
                        <Card className="border border-orange-200 bg-orange-50/50 overflow-hidden">
                          <div className="bg-orange-100 px-6 py-4 border-b border-orange-200">
                            <div className="flex items-center space-x-3">
                              <AlertCircle className="w-6 h-6 text-orange-600" />
                              <div>
                                <h4 className="text-gray-900">Missing Mandatory Information</h4>
                                <p className="text-sm text-gray-600">The following required fields need to be completed</p>
                              </div>
                            </div>
                          </div>
                          <div className="p-6">
                            <div className="space-y-3">
                              {missingFields.map((field, index) => (
                                <motion.div
                                  key={field.label}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: index * 0.1 }}
                                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-200"
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <AlertCircle className="w-4 h-4 text-orange-600" />
                                      <span className="text-sm text-gray-700">{field.label}</span>
                                    </div>
                                    <p className="text-xs text-gray-500">Required for {joinerType} onboarding</p>
                                  </div>
                                  <Badge variant="outline" className="text-orange-600 border-orange-300">
                                    Required
                                  </Badge>
                                </motion.div>
                              ))}
                            </div>
                            <div className="mt-4 p-3 bg-white rounded-lg border border-orange-200">
                              <p className="text-sm text-gray-600">
                                <strong>Note:</strong> You can still submit for initial review. HR will request these missing details during the verification process.
                              </p>
                            </div>
                          </div>
                        </Card>
                      )}

                      {/* Extracted Mandatory Fields */}
                      <Card className="border border-gray-200 overflow-hidden">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            <h4 className="text-gray-900">Extracted Mandatory Information</h4>
                            <div className="flex items-center space-x-2">
                              <Badge className={joinerInfo.type.color}>
                                {joinerInfo.type.title}
                              </Badge>
                              {joinerType === 'employee' && (
                                <Badge className={joinerInfo.experience.color}>
                                  {joinerInfo.experience.title}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="p-6">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Personal Information */}
                            <div>
                              <div className="flex items-center space-x-2 mb-4">
                                <User className="w-5 h-5 text-blue-600" />
                                <h5 className="text-gray-900">Personal Information</h5>
                              </div>
                              <div className="space-y-4">
                                {extractedFields.filter(field => 
                                  ['Full Name', 'Date of Birth', 'Personal Email', 'Mobile Number'].includes(field.label) && !field.isMissing && field.value
                                ).map((field, index) => (
                                  <motion.div
                                    key={field.label}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                  >
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2 mb-1">
                                        <span className="text-sm text-gray-700">{field.label}</span>
                                        <Check className="w-3 h-3 text-green-600" />
                                      </div>
                                      <p className="text-gray-900">{field.value}</p>
                                      <div className="flex items-center space-x-2 mt-1">
                                        <span className="text-xs text-gray-500">Source: {field.source}</span>
                                        <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                                          Verified
                                        </Badge>
                                      </div>
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                            </div>

                            {/* Identity Documents */}
                            <div>
                              <div className="flex items-center space-x-2 mb-4">
                                <CreditCard className="w-5 h-5 text-purple-600" />
                                <h5 className="text-gray-900">Identity Documents</h5>
                              </div>
                              <div className="space-y-4">
                                {extractedFields.filter(field => 
                                  ['PAN Number', 'Passport Number'].includes(field.label) && !field.isMissing && field.value
                                ).map((field, index) => (
                                  <motion.div
                                    key={field.label}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: (index + 4) * 0.1 }}
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                  >
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2 mb-1">
                                        <span className="text-sm text-gray-700">{field.label}</span>
                                        <Check className="w-3 h-3 text-green-600" />
                                      </div>
                                      <p className="text-gray-900">{field.value}</p>
                                      <div className="flex items-center space-x-2 mt-1">
                                        <span className="text-xs text-gray-500">Source: {field.source}</span>
                                        <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                                          Verified
                                        </Badge>
                                      </div>
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                            </div>

                            {/* Professional/Academic Information */}
                            {extractedFields.some(field => 
                              ['Highest Qualification', 'Institution', 'Graduation Year', 'CGPA/Percentage', 'Total Work Experience', 'Total Professional Experience', 'Last Company Name', 'Last Designation', 'Employment Tenure', 'Billing Rate/Consulting Fee'].includes(field.label)
                            ) && (
                              <div>
                                <div className="flex items-center space-x-2 mb-4">
                                  {joinerType === 'consultant' || (joinerType === 'employee' && experienceLevel === 'experienced') ? (
                                    <Briefcase className="w-5 h-5 text-orange-600" />
                                  ) : (
                                    <GraduationCap className="w-5 h-5 text-green-600" />
                                  )}
                                  <h5 className="text-gray-900">
                                    {joinerType === 'consultant' ? 'Professional Information' : 
                                     joinerType === 'employee' && experienceLevel === 'experienced' ? 'Professional Information' :
                                     'Academic Information'}
                                  </h5>
                                </div>
                                <div className="space-y-4">
                                  {extractedFields.filter(field => 
                                    ['Highest Qualification', 'Institution', 'Graduation Year', 'CGPA/Percentage', 'Total Work Experience', 'Total Professional Experience', 'Last Company Name', 'Last Designation', 'Employment Tenure', 'Billing Rate/Consulting Fee'].includes(field.label) && !field.isMissing && field.value
                                  ).map((field, index) => (
                                    <motion.div
                                      key={field.label}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: (index + 6) * 0.1 }}
                                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                    >
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                          <span className="text-sm text-gray-700">{field.label}</span>
                                          <Check className="w-3 h-3 text-green-600" />
                                        </div>
                                        <p className="text-gray-900">{field.value}</p>
                                        <div className="flex items-center space-x-2 mt-1">
                                          <span className="text-xs text-gray-500">Source: {field.source}</span>
                                          <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                                            Verified
                                          </Badge>
                                        </div>
                                      </div>
                                    </motion.div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Contact & Address Information */}
                            {extractedFields.some(field => 
                              ['Current Residential Address', 'Emergency Contact Name', 'Emergency Contact Phone', 'Bank Account Number', 'Bank IFSC Code'].includes(field.label)
                            ) && (
                              <div>
                                <div className="flex items-center space-x-2 mb-4">
                                  <MapPin className="w-5 h-5 text-red-600" />
                                  <h5 className="text-gray-900">Contact & Banking Information</h5>
                                </div>
                                <div className="space-y-4">
                                  {extractedFields.filter(field => 
                                    ['Current Residential Address', 'Emergency Contact Name', 'Emergency Contact Phone', 'Bank Account Number', 'Bank IFSC Code'].includes(field.label) && !field.isMissing && field.value
                                  ).map((field, index) => (
                                    <motion.div
                                      key={field.label}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: (index + 8) * 0.1 }}
                                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                    >
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                          <span className="text-sm text-gray-700">{field.label}</span>
                                          <Check className="w-3 h-3 text-green-600" />
                                        </div>
                                        <p className="text-gray-900">{field.value}</p>
                                        <div className="flex items-center space-x-2 mt-1">
                                          <span className="text-xs text-gray-500">Source: {field.source}</span>
                                          <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                                            Verified
                                          </Badge>
                                        </div>
                                      </div>
                                    </motion.div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Summary Footer */}
                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span>Total Fields: {extractedFields.length}</span>
                              <span>•</span>
                              <span>Completed: {completedFields.length}</span>
                              <span>•</span>
                              <span className={missingFields.length === 0 ? 'text-green-600' : 'text-orange-600'}>
                                {missingFields.length === 0 ? '✓ All Required Complete' : `${missingFields.length} Missing`}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Sparkles className="w-4 h-4 text-purple-600" />
                              <span className="text-sm text-purple-600">Powered by TurfAI</span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Submit Button */}
                <motion.div
                  className="pt-4"
                  animate={launchAnimation ? { 
                    y: [0, -20, 0],
                    scale: [1, 1.05, 1]
                  } : {}}
                  transition={{ duration: 0.8 }}
                >
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-4 text-lg relative overflow-hidden"
                  >
                    {isSubmitting ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-5 h-5 mr-3"
                        >
                          <Sparkles className="w-5 h-5" />
                        </motion.div>
                        Launching to Mission Control...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-3" />
                        Submit for HR Review
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
                          animate={{ x: ["-100%", "100%"] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        />
                      </>
                    )}
                  </Button>
                </motion.div>
              </motion.div>
            </motion.div>
          ) : (
            // Success State
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center max-w-2xl mx-auto"
            >
              <motion.div 
                className="mb-8"
                animate={{ 
                  y: [0, -15, 0],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <AstroMascot expression="excited" size="xl" className="mx-auto" />
              </motion.div>

              <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 p-8">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mx-auto flex items-center justify-center mb-6"
                >
                  <CheckCircle className="w-10 h-10 text-white" />
                </motion.div>
                
                <h2 className="text-gray-900 mb-4">
                  🚀 Successfully Launched!
                </h2>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Your onboarding has been submitted to Mission Control! HR will review your 
                  documents and get back to you within 24 hours. 
                </p>

                <div className="bg-white rounded-lg p-4 border border-green-200 mb-6">
                  <div className="flex items-center justify-center space-x-2 text-green-600">
                    <Rocket className="w-5 h-5" />
                    <span className="text-sm">Submission ID: #DayOne-{Date.now().toString().slice(-6)}</span>
                  </div>
                </div>

                <motion.div
                  className="inline-flex items-center space-x-2 text-purple-600"
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Clock className="w-5 h-5" />
                  <span className="text-sm">Redirecting to next phase...</span>
                </motion.div>
              </Card>
            </motion.div>
          )}
        </div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/70 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}